import React from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import type { IntegrationProvider, IntegrationConfig } from '../../types/integrations';

interface IntegrationCardProps {
  provider: IntegrationProvider;
  config?: IntegrationConfig;
  onClick: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  provider,
  config,
  onClick
}) => {
  const getStatusIcon = () => {
    if (!config) return null;
    
    switch (config.status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'configuring':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    if (!config) return 'Não configurado';
    
    switch (config.status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'error':
        return 'Erro';
      case 'configuring':
        return 'Configurando';
      default:
        return 'Desconhecido';
    }
  };

  const getStatusColor = () => {
    if (!config) return 'bg-gray-100 text-gray-800';
    
    switch (config.status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'configuring':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {provider.name}
          </h3>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`}>
            {getStatusIcon()}
            <span className="ml-1">{getStatusText()}</span>
          </span>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        {provider.description}
      </p>

      {config?.lastSync && (
        <p className="text-xs text-gray-400">
          Última sincronização: {new Date(config.lastSync).toLocaleString()}
        </p>
      )}

      {provider.docsUrl && (
        <a
          href={provider.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:text-blue-800 mt-2 inline-block"
          onClick={(e) => e.stopPropagation()}
        >
          Documentação →
        </a>
      )}
    </div>
  );
};

export default IntegrationCard;